import { useCallback, useEffect, useMemo, useState } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import { ReportTemplatesAPI } from "../../../api/ReportTemplates";
import {
  I, getToken, getErrorMessage, showExportToast, safeFileName,
  getFilenameFromDisposition, downloadBlob, ExportModal, toISO, ago,
} from "../ReportsPanel";
import LegacyFilterBar, { getUserBranchesWithIds } from "./LegacyFilterBar";
import { RenderLegacyResult } from "./legacyResultRenderers";
import { getLegacyPdfBuilder } from "../../../../shared/pdf/legacyReportPdfBuilders";

// Vista previa/ejecucion para plantillas SMARTPAY/LEGACY - paralela a PreviewView (METRICS)
// pero usando /report-templates/:id/run + /report-templates/:id/export-legacy-excel en vez
// del motor de metricas, y el registry de renderers en vez de bloques/Recharts.
export default function LegacyPreviewView({ template, catalog, businessCode, onBack }) {
  const entry = (catalog || []).find((e) => e.code === template.legacy_report_code) || null;
  const branches = useMemo(getUserBranchesWithIds, []);
  const savedFilters = template.filters || {};

  const [dFrom, setDFrom] = useState(savedFilters.minDate || ago(7));
  const [dTo, setDTo] = useState(savedFilters.maxDate || toISO());
  const [quickIndex, setQuickIndex] = useState(null);
  const [branchCodes, setBranchCodes] = useState(() => {
    const saved = savedFilters.branchoffice_code;
    if (Array.isArray(saved) && saved.length) return saved;
    if (typeof saved === "string" && saved && saved !== "0") return saved.split(",");
    return branches.map((b) => b.code);
  });
  const [cashierId, setCashierId] = useState(savedFilters.cashier_id || null);
  const [waiterId, setWaiterId] = useState(savedFilters.waiter_id || null);
  const [eventId, setEventId] = useState(savedFilters.event_id || null);
  const [machineId, setMachineId] = useState(savedFilters.machine_id || null);
  const [paymentMethodId, setPaymentMethodId] = useState(savedFilters.payment_method_id || null);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState("");

  const onDateChange = (from, to, qi) => { setDFrom(from); setDTo(to); setQuickIndex(qi); };

  const currentFilters = useMemo(() => ({
    minDate: dFrom,
    maxDate: dTo,
    branchoffice_codes: branchCodes.length > 1 ? branchCodes : undefined,
    cashier_id: cashierId || undefined,
    waiter_id: waiterId || undefined,
    event_id: eventId || undefined,
    machine_id: machineId || undefined,
    payment_method_id: paymentMethodId || undefined,
  }), [dFrom, dTo, branchCodes, cashierId, waiterId, eventId, machineId, paymentMethodId]);

  const templateId = template._id || template.id;
  const branchOverride = branchCodes.length === 1 ? branchCodes[0] : "0";

  const runReport = useCallback(async () => {
    if (!templateId || !dFrom || !dTo) return;
    setLoading(true);
    setError("");
    try {
      const response = await ReportTemplatesAPI.runTemplate(templateId, {
        business_code: businessCode,
        branchoffice_code: branchOverride,
        filters: currentFilters,
      }, getToken());
      setPayload(response?.data || null);
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo ejecutar el reporte"));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [templateId, businessCode, branchOverride, currentFilters, dFrom, dTo]);

  useEffect(() => { runReport(); }, [runReport]);

  const handleExportExcel = async () => {
    setExporting("excel");
    try {
      const response = await ReportTemplatesAPI.exportLegacyExcel(templateId, {
        business_code: businessCode,
        branchoffice_code: branchOverride,
        filters: currentFilters,
      }, getToken());
      const headerName = getFilenameFromDisposition(response.headers?.["content-disposition"]);
      const filename = headerName || `${safeFileName(template.name)}.xlsx`;
      downloadBlob(response.data, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
      setExportModalOpen(false);
    } catch (err) {
      showExportToast(getErrorMessage(err, "No se pudo exportar Excel"));
    } finally {
      setExporting("");
    }
  };

  const handleExportPdf = () => {
    setExporting("pdf");
    try {
      const buildPdf = getLegacyPdfBuilder(template.legacy_report_code);
      if (!buildPdf) throw new Error("Este reporte no tiene exportacion a PDF");
      const branchLabel = branchCodes.length === branches.length || branchCodes.length === 0
        ? "Todas"
        : branchCodes.map((c) => branches.find((b) => b.code === c)?.name || c).join(", ");
      const docDefinition = buildPdf(payload, { filters: currentFilters, branchLabel, templateName: template.name });
      pdfMake.createPdf(docDefinition).download(`${safeFileName(template.name)}.pdf`);
      setExportModalOpen(false);
    } catch (err) {
      showExportToast(getErrorMessage(err, "No se pudo exportar PDF"));
    } finally {
      setExporting("");
    }
  };

  if (!entry) {
    return (
      <>
        <div className="rb-top-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="rb-back-btn" onClick={onBack}><I.ArrowLeft /></button>
            <div className="rb-top-title">{template.name}</div>
          </div>
        </div>
        <div className="rb-empty"><div className="rb-empty__title">Reporte SmartPay no encontrado</div><div className="rb-empty__desc">El catálogo no reconoce el código "{template.legacy_report_code}".</div></div>
      </>
    );
  }

  return (
    <>
      <div className="rb-top-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="rb-back-btn" onClick={onBack}><I.ArrowLeft /></button>
          <div>
            <div className="rb-top-title">{template.name}</div>
            <div style={{ fontSize: 12, color: "#9090A0" }}>Plantilla SmartPay · {entry.name}</div>
          </div>
        </div>
        {(entry.capabilities?.excel || entry.capabilities?.pdf) && (
          <button className="rb-btn-secondary" onClick={() => setExportModalOpen(true)}><I.Download /> Exportar</button>
        )}
      </div>

      <LegacyFilterBar
        entry={entry}
        dFrom={dFrom} dTo={dTo} quickIndex={quickIndex} onDateChange={onDateChange}
        branchCodes={branchCodes} onBranchChange={setBranchCodes}
        cashierId={cashierId} onCashierChange={setCashierId}
        waiterId={waiterId} onWaiterChange={setWaiterId}
        eventId={eventId} onEventChange={setEventId}
        machineId={machineId} onMachineChange={setMachineId}
        paymentMethodId={paymentMethodId} onPaymentMethodChange={setPaymentMethodId}
      />

      {error && <div style={{ fontSize: 12, color: "#C53030", margin: "10px 0" }}>{error}</div>}
      {loading && <div className="rb-empty"><div className="rb-empty__title">Cargando datos...</div></div>}
      {!loading && !error && <RenderLegacyResult reportCode={template.legacy_report_code} payload={payload} />}

      <ExportModal
        open={exportModalOpen}
        exporting={exporting}
        onClose={() => !exporting && setExportModalOpen(false)}
        onExcel={entry.capabilities?.excel ? handleExportExcel : undefined}
        onPdf={entry.capabilities?.pdf ? handleExportPdf : undefined}
      />
    </>
  );
}
